import { IWikifolioDetailData } from "src/api/server/wf-detail.types";
import { getGlobals } from "src/constants/globals";
import { createWikifolioDetailProductSchema } from "src/constants/structured-data";
import { EnumWikifolioStatus } from "src/types/common-enum";
import { JsonLdScript } from "./json-ld-script";

const getLicensedPrimaryIsin = (certificates: IWikifolioDetailData["certificates"]) => {
    const primaryCertificate = certificates.find(certificate => certificate.isPrimary && !certificate.isHidden);

    return primaryCertificate?.isLicensed ? primaryCertificate.isin : null;
};

const getKeywords = (tagsData: IWikifolioDetailData["tagsData"]) => {
    if (!tagsData) {
        return [];
    }

    const labels = Object.values(tagsData)
        .flat()
        .map(tag => tag.label);

    return Array.from(new Set(labels));
};

interface IWikifolioDetailProductSchemaProps {
    canonicalUrl: string;
    data: Pick<IWikifolioDetailData, "wikifolio" | "trader" | "keyFigures" | "masterData" | "certificates" | "tagsData" | "structuredData">;
}

export const WikifolioDetailProductSchema = ({ canonicalUrl, data }: IWikifolioDetailProductSchemaProps) => {
    if (data.wikifolio.status !== EnumWikifolioStatus.Investable) {
        return null;
    }

    const totalInvestments = data.keyFigures.totalInvestments.ranking.value;

    const schema = createWikifolioDetailProductSchema({
        canonicalUrl,
        name: data.wikifolio.shortDescription,
        descriptionContent: data.wikifolio.description.content,
        keywords: getKeywords(data.tagsData),
        sku: data.wikifolio.symbol.toLowerCase(),
        isin: getLicensedPrimaryIsin(data.certificates),
        traderNickName: data.trader.nickName,
        tradingCurrency: data.wikifolio.currency,
        investedCapital: totalInvestments !== null ? { value: totalInvestments, currency: getGlobals().currency } : null,
        creationDate: data.masterData.creationDate.value,
        image: data.wikifolio.chartImageUrl,
        price: data.structuredData.price,
        priceCurrency: data.structuredData.currency,
        performanceSinceCreation: data.structuredData.performanceSinceCreation,
        averageAnnualPerformance: data.structuredData.averageAnnualPerformance,
        performanceOneYear: data.structuredData.performanceOneYear,
        volatilityOneYear: data.structuredData.volatilityOneYear,
    });

    return <JsonLdScript schema={schema} />;
};
